import { USStateOptions } from '@strata/constants';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { useFormikContext } from 'formik';
import type * as Yup from 'yup';

import {
    type FormSections,
    type IntakeFormSectionSchemas,
} from 'store/intakeForm/intakeFormSchema';

type Section = typeof FormSections.PATIENT;

interface PatientInputsProps {
    disabled?: boolean;
}

const Gender = {
    M: 'Male',
    F: 'Female',
    U: 'Undifferentiated',
} as const;

const genderOptions = Object.entries(Gender).map(([code, label]) => ({
    value: code,
    name: label,
}));

export const PatientInputs = ({ disabled = false }: PatientInputsProps) => {
    const formik = useFormikContext<Yup.InferType<(typeof IntakeFormSectionSchemas)[Section]>>();

    return (
        <div className="sds_intake-form-grid">
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <TextInputFormik
                        id="memberId"
                        label="Member Id"
                        required
                        disabled={disabled}
                        inputCopyButton
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <SuggestSingleSelectFormik
                        id="memberGender"
                        label="Gender"
                        placeholderText="Select"
                        values={genderOptions}
                        onChange={(newValue) => {
                            (() => formik.setFieldValue('memberGender', newValue?.value))();
                        }}
                        selectedItem={genderOptions.find(
                            (v) => v.value === formik.values.memberGender,
                        )}
                        disabled={disabled}
                        disableAutoPopulate
                        required
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                    <TextInputFormik
                        id="memberAddress1"
                        label="Address Line 1"
                        disabled={disabled}
                        required
                        inputCopyButton
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                    <TextInputFormik
                        id="memberAddress2"
                        label="Address Line 2"
                        disabled={disabled}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <TextInputFormik id="memberCity" label="City" required disabled={disabled} />
                </div>
                <div style={{ gridColumn: '2', minWidth: 0 }}>
                    <SuggestSingleSelectFormik
                        id="memberState"
                        label="State"
                        required
                        disabled={disabled}
                        values={USStateOptions}
                        onChange={(newVal) => {
                            (async () =>
                                formik.setFieldValue('memberState', newVal?.secondaryData))();
                        }}
                        selectedItem={USStateOptions.find(
                            ({ secondaryData }) => secondaryData === formik.values.memberState,
                        )}
                    />
                </div>
                <div style={{ gridColumn: '3', minWidth: 0 }}>
                    <TextInputFormik
                        id="memberZipcode"
                        label="Zip Code"
                        required
                        disabled={disabled}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <PhoneInputFormik
                        id="memberPhoneNumber"
                        label="Patient Phone Number"
                        disabled={disabled}
                        inputCopyButton
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <TextInputFormik id="bin" label="Bin" disabled={disabled} />
                </div>
                <div style={{ gridColumn: '2 / -1', minWidth: 0 }}>
                    <TextInputFormik id="pcn" label="Pcn" disabled={disabled} />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <TextInputFormik id="rxGroup" label="Rx Group" disabled={disabled} />
                </div>
            </div>
        </div>
    );
};
